/**
 * Weapon domain types — the catalog entry used by the GvG planner.
 *
 *   - `WeaponRole`        — the role of an individual weapon (TANK / DPS /
 *                           HEAL). Drives catalog ordering, filter chips, and
 *                           weapon-path colors.
 *   - `PlayerRole`        — the role derived from a player's equipped pair.
 *                           It additionally includes HYBRID; see its contract
 *                           below.
 *   - `WeaponRoleColor`   — the role-tinted color string used as a Tailwind class
 *                           key for borders and accent strips on weapon icons.
 *                           Has a stable 1:1 mapping to `WeaponRole`; see
 *                           `roleToColor` below.
 *   - `WeaponSubPath`     — the 11 recorded in-game martial-art paths
 *                           (bellstrike / stonesplit / silkbind / bamboocut).
 *                           It describes a weapon's path; standard weapon
 *                           displays use the matching path badge while the
 *                           selector also has per-weapon artwork by slug.
 *   - `WeaponType`        — the 9 in-game weapon categories (sword / spear /
 *                           mo-blade / fan / umbrella / dual-blades / rope-dart /
 *                           gauntlets / waist-drum).
 */

export const WeaponRole = {
  Tank: 'TANK',
  Dps: 'DPS',
  Heal: 'HEAL',
} as const

export type WeaponRole = (typeof WeaponRole)[keyof typeof WeaponRole]

/**
 * Persisted role for a player and raid snapshot, derived from `mainWeapons`:
 *
 * - Thundercry Blade → TANK (takes precedence over every other weapon)
 * - Panacea Fan + Soulshade Umbrella → HEAL
 * - exactly one of Panacea Fan / Soulshade Umbrella → HYBRID
 * - every other loadout → DPS
 *
 * Individual weapon catalog rows deliberately remain `WeaponRole`, because
 * HYBRID describes the pair rather than a single weapon or a path color.
 */
export const PlayerRole = {
  Tank: 'TANK',
  Dps: 'DPS',
  Hybrid: 'HYBRID',
  Heal: 'HEAL',
} as const

export type PlayerRole = (typeof PlayerRole)[keyof typeof PlayerRole]

export const WeaponRoleColor = {
  Tank: 'tank',
  Dps: 'dps',
  Heal: 'heal',
} as const

export type WeaponRoleColor = (typeof WeaponRoleColor)[keyof typeof WeaponRoleColor]

/**
 * Stable 1:1 mapping from `WeaponRole` to its `WeaponRoleColor` key.
 * Implemented with an exhaustive `switch` so adding a new `WeaponRole`
 * member causes a TypeScript compile error until the mapping is updated.
 */
export function roleToColor(role: WeaponRole): WeaponRoleColor {
  switch (role) {
    case WeaponRole.Tank:
      return WeaponRoleColor.Tank
    case WeaponRole.Dps:
      return WeaponRoleColor.Dps
    case WeaponRole.Heal:
      return WeaponRoleColor.Heal
  }
}

export const WeaponSubPath = {
  BellstrikeSplendor: 'bellstrike_splendor',
  BellstrikeUmbra: 'bellstrike_umbra',
  StonesplitMight: 'stonesplit_might',
  StonesplitStrength: 'stonesplit_strength',
  SilkbindJade: 'silkbind_jade',
  SilkbindDeluge: 'silkbind_deluge',
  BamboocutWind: 'bamboocut_wind',
  BamboocutDust: 'bamboocut_dust',
  BamboocutKite: 'bamboocut_kite',
  BamboocutDraught: 'bamboocut_draught',
  SilkbindWings: 'silkbind_wings',
} as const

export type WeaponSubPath = (typeof WeaponSubPath)[keyof typeof WeaponSubPath]

export const WeaponType = {
  Sword: 'sword',
  Spear: 'spear',
  MoBlade: 'mo-blade',
  Fan: 'fan',
  Umbrella: 'umbrella',
  DualBlades: 'dual-blades',
  RopeDart: 'rope-dart',
  Gauntlets: 'gauntlets',
  WaistDrum: 'waist-drum',
} as const

export type WeaponType = (typeof WeaponType)[keyof typeof WeaponType]

export interface Weapon {
  /** Canonical slug — also the selector artwork filename (`assets/weapons/<slug>.png`). */
  readonly slug: string
  readonly name: string
  readonly cn: string
  readonly type: WeaponType
  readonly role: WeaponRole
  /** Role-tinted color used for borders and accent strips on the weapon icon. */
  readonly color: WeaponRoleColor
  /** Martial-art path assigned to this weapon. */
  readonly subPath: WeaponSubPath
  /** Public path for this weapon's martial-art path badge, including the `.png` extension. */
  readonly icon: string
  /** Selector-only public path for this weapon's direct artwork, including the `.png` extension. */
  readonly artwork: string
  readonly description: string
}
